'use client';
import React, { useState } from 'react';
import Link from 'next/link';

import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import Badge from '@/components/shared/Badge';
import RemovedContent from '@/components/shared/RemovedContent';
import ReportModal from '@/components/shared/ReportModal';
import BlockModal from '@/components/shared/BlockModal';
import ShareModal from '@/components/shared/ShareModal';
import { mockResidents, Resident } from '@/data/mockResidents';
import { ArrowLeft, Mail, Phone, ExternalLink, CheckCircle, Share2 } from 'lucide-react';

function getRoleBadgeVariant(role: Resident['role']) {
  if (role === 'Owner') return 'owner' as const;
  if (role === 'Renter') return 'renter' as const;
  if (role === 'Multiple Unit Owner') return 'multipleUnitOwner' as const;
  return 'resident' as const;
}

function AvatarInitials({ name, size = 'lg' }: { name: string; size?: 'lg' | 'xl' }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const dim = size === 'xl' ? 'w-20 h-20 text-[22px]' : 'w-14 h-14 text-[16px]';
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold`}
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.25)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg key={star} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 1L7.35 4.36L11 4.64L8.5 6.86L9.27 10.5L6 8.5L2.73 10.5L3.5 6.86L1 4.64L4.65 4.36L6 1Z"
            fill={star <= Math.round(rating) ? '#F0A832' : 'rgba(167,163,196,0.20)'}
          />
        </svg>
      ))}
    </div>
  );
}

interface ProfilePageProps {
  params: { profileSlug: string };
}

const BLOCKED_SLUGS = ['blocked-user'];
const REMOVED_SLUGS = ['removed-user'];

export default function ProfilePage({ params }: ProfilePageProps) {
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  if (BLOCKED_SLUGS.includes(params.profileSlug)) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="profile" title="Profile Unavailable" description="You have blocked this resident, or this profile is not available to you." backHref="/directory" backLabel="Back to Directory" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (REMOVED_SLUGS.includes(params.profileSlug)) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="profile" backHref="/directory" backLabel="Back to Directory" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const resident = mockResidents.find((r) => r.slug === params.profileSlug);

  if (!resident) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="profile" title="Profile Not Found" description="This resident profile doesn't exist or may have been removed." backHref="/directory" backLabel="Back to Directory" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
            <Link
              href="/directory"
              className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80"
              style={{ color: '#A7A3C4' }}
            >
              <ArrowLeft size={14} />
              Back to Directory
            </Link>
          </div>
        </div>

        <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main profile */}
            <div className="lg:col-span-2 space-y-6">
              {/* Profile header */}
              <div className="neon-card p-6">
                <div className="flex items-start gap-5">
                  <AvatarInitials name={resident.name} size="xl" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h1 className="font-display font-bold text-[24px] leading-tight mb-2" style={{ color: '#F2F4FF' }}>
                          {resident.name}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={getRoleBadgeVariant(resident.role)} label={resident.role} size="md" />
                          {resident.isVerified && <Badge variant="verified" size="md" />}
                          {resident.vouchedBy && <Badge variant="vouched" size="md" />}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1">
                      <p className="text-[13.5px]" style={{ color: '#A7A3C4' }}>
                        {resident.tower} · Unit {resident.unit}
                      </p>
                      {resident.profession && (
                        <p className="text-[14px] font-medium" style={{ color: 'rgba(242,244,255,0.80)' }}>
                          {resident.profession}
                        </p>
                      )}
                      {resident.joinedYear && (
                        <p className="text-[12.5px]" style={{ color: 'rgba(167,163,196,0.55)' }}>
                          Panorama resident since {resident.joinedYear}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {resident.vouchedBy && (
                  <div
                    className="mt-5 flex items-center gap-2.5 px-4 py-3 rounded-lg"
                    style={{ background: 'rgba(45,212,160,0.06)', border: '1px solid rgba(45,212,160,0.15)' }}
                  >
                    <CheckCircle size={15} style={{ color: '#2DD4A0' }} />
                    <p className="text-[13px]" style={{ color: '#2DD4A0' }}>
                      Vouched by <span className="font-semibold">{resident.vouchedBy}</span>
                    </p>
                  </div>
                )}

                {resident.bio && (
                  <div className="mt-5 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>
                      {resident.bio}
                    </p>
                  </div>
                )}
              </div>

              {/* Services offered */}
              {resident.servicesOffered && resident.servicesOffered.length > 0 && (
                <div className="neon-card p-6">
                  <h2 className="font-display font-semibold text-[16px] mb-4" style={{ color: '#F2F4FF' }}>
                    Services Offered
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {resident.servicesOffered.map((s) => (
                      <span
                        key={s}
                        className="text-[13px] px-3 py-1.5 rounded-lg font-medium"
                        style={{ background: 'rgba(122,92,255,0.10)', color: '#9B7FFF', border: '1px solid rgba(122,92,255,0.18)' }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 text-[12.5px]" style={{ color: 'rgba(167,163,196,0.55)' }}>
                    Contact this resident to inquire about services.
                  </p>
                </div>
              )}

              {/* Interests */}
              {resident.interests && resident.interests.length > 0 && (
                <div className="neon-card p-6">
                  <h2 className="font-display font-semibold text-[16px] mb-4" style={{ color: '#F2F4FF' }}>
                    Interests
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {resident.interests.map((interest) => (
                      <span
                        key={interest}
                        className="text-[13px] px-3 py-1.5 rounded-lg"
                        style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Contact */}
              <div className="neon-card p-5">
                <h3 className="font-display font-semibold text-[14px] mb-4" style={{ color: '#F2F4FF' }}>
                  Contact
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail size={15} style={{ color: '#A7A3C4' }} />
                    {resident.emailVisible && resident.email ? (
                      <span className="text-[13px]" style={{ color: '#4CC9FF' }}>{resident.email}</span>
                    ) : (
                      <span className="text-[13px] italic" style={{ color: 'rgba(167,163,196,0.45)' }}>
                        Email hidden by resident
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={15} style={{ color: '#A7A3C4' }} />
                    {resident.phoneVisible && resident.phone ? (
                      <span className="text-[13px]" style={{ color: '#4CC9FF' }}>{resident.phone}</span>
                    ) : (
                      <span className="text-[13px] italic" style={{ color: 'rgba(167,163,196,0.45)' }}>
                        Phone hidden by resident
                      </span>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-[11.5px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.45)' }}>
                  Contact details are controlled by the resident. Reach out via the Conversations section.
                </p>
              </div>

              {/* Social links */}
              {resident.socialLinks && resident.socialLinks.length > 0 && (
                <div className="neon-card p-5">
                  <h3 className="font-display font-semibold text-[14px] mb-4" style={{ color: '#F2F4FF' }}>
                    Social
                  </h3>
                  <div className="space-y-2">
                    {resident.socialLinks.map((link) => (
                      <a
                        key={link.platform}
                        href={link.url}
                        className="flex items-center gap-2.5 text-[13px] transition-all duration-150 hover:text-[#4CC9FF] active:opacity-70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(76,201,255,0.35)] rounded px-1 py-0.5"
                        style={{ color: '#A7A3C4' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink size={13} />
                        {link.platform}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="neon-card p-5 space-y-2">
                <button
                  onClick={() => setShareOpen(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 hover:bg-white/[0.06] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.35)]"
                  style={{ color: '#A7A3C4', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <Share2 size={14} />
                  Share profile
                </button>
                <button
                  onClick={() => setReportOpen(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 hover:bg-white/[0.06] hover:text-[#F2F4FF] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.35)]"
                  style={{ color: '#A7A3C4', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <ArrowLeft size={14} className="rotate-180" />
                  Report this profile
                </button>
                <button
                  onClick={() => setBlockOpen(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 hover:bg-[rgba(192,57,43,0.12)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(192,57,43,0.35)]"
                  style={{ color: '#FF6B6B', background: 'transparent', border: '1px solid rgba(192,57,43,0.20)' }}
                >
                  Block this resident
                </button>
              </div>
            </div>
          </div>
        </div>

        <ReportModal
          isOpen={reportOpen}
          onClose={() => setReportOpen(false)}
          contentType="profile"
          contentLabel={resident.name}
        />
        <BlockModal
          isOpen={blockOpen}
          onClose={() => setBlockOpen(false)}
          userName={resident.name}
        />
        <ShareModal
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          contentType="profile"
          title={resident.name}
        />
      </main>
      <SiteFooter />
    </div>
  );
}
